import { useCallback, useEffect, useState } from 'react'
import { useProjectCustomFieldsQuery } from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useAuthContext } from 'context/AuthContext'
import { CustomFieldDataType, CustomListItemStyle } from '@cims-online/shared'
import { createOptionalContext } from 'utils/createContext'
import { DisabledReason } from 'hooks/useDisabledOptions'
import { ProjectCustomField, ProjectCustomFieldsContext } from './types'
import { NetworkStatus } from '@apollo/client'
import { toMutateCustomFieldsInput } from './utils/toMutateCustomFieldsInput'
import { toCustomFieldEditorValue } from './utils/toCustomFieldEditorValue'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } =
  createOptionalContext<ProjectCustomFieldsContext>({
    defaults: {
      __initialized: false,
      classType: undefined,
      customFields: [],
      customFieldIdToProjectCustomField: new Map(),
      renderedAsCheckboxes: { checkboxes: [], booleans: [] },
      fetching: false,
      refetch: () =>
        Promise.resolve({
          networkStatus: NetworkStatus.ready,
          data: {
            __typename: 'Query',
            customFields: {
              __typename: 'CustomFieldListResponse',
              records: [],
            },
          },
          loading: false,
        }),
      disabledById: () => [],
      toCustomFieldEditorValue: () => undefined,
      toMutateCustomFieldsInput: () => ({}),
      editorOnSome: false,
    },
    displayName: 'ProjectCustomFieldsContext',
    usingDefaults: ({ __initialized }) => !__initialized,
  })

/**
 * wraps and makes use of the {@link ProjectCustomFieldsQuery} through
 * {@link useProjectCustomFieldsQuery}
 *
 * to use this context, you must wrap your components in {@link ProjectCustomFieldsProvider}
 * and import it in the wrapped component
 *
 * @see {@link createOptionalContext}
 * @example
 * <CustomFieldProvider>
 *  <FooComponent />
 * </CustomFieldProvider>
 * ...
 * const FooComponent: React.FC = () => {
 *   const { data } = useCustomFields()
 * }
 */
export const useProjectCustomFieldsContext = useContextFn

export const ProjectCustomFieldsProvider: React.FC<
  Partial<Pick<ProjectCustomFieldsContext, 'classType'>>
> = ({ classType, children }) => {
  const { projectId } = useProjectContext()
  const {
    data,
    loading: fetching,
    refetch,
  } = useProjectCustomFieldsQuery({
    ...DefaultQueryHookOptions,
    variables: {
      projectId,
      customFieldToClassTypeFieldInput: classType
        ? { deleted: false, classTypeId: { equals: classType.id } }
        : { deleted: false },
    },
  })
  const { user } = useAuthContext()

  const disabledById = useCallback<ProjectCustomFieldsContext['disabledById']>(
    ({ alreadyDisabled, customFieldId, recordInactive }) => {
      if (!data?.customFields?.records) return []
      if (!classType) return []
      if (recordInactive)
        return [
          {
            disabled: recordInactive,
            reason: {
              why: 'inactive',
            },
          },
        ]
      const cf2ct = data?.customFields?.records
        ?.flatMap(({ customFieldToClassTypes }) => customFieldToClassTypes)
        .find(
          (cf2ct) =>
            cf2ct.classTypeId === classType.id &&
            cf2ct.customFieldId === customFieldId
        )
      const editor =
        !cf2ct?.users.length && !cf2ct?.roles.length
          ? true
          : user?.implicitlyAuthorized({ ...cf2ct, projectId }).authorized ??
            false
      return [
        {
          disabled: !editor,
          reason: {
            why: 'disabled',
            message:
              'You have not been assigned as an editor on this Custom Field',
          },
        },
        {
          disabled: alreadyDisabled !== false && !cf2ct?.alwaysEditable,
          // We can safely make this cast because we're checking disabled in a parser
          reason: alreadyDisabled as DisabledReason,
        },
      ]
    },
    [classType, data?.customFields?.records, user, projectId]
  )

  const [editorOnSome, setEditorOnSome] = useState(false)
  useEffect(() => {
    if (!classType) return
    if (!data?.customFields?.records) return
    const cf2cts = data.customFields.records.flatMap(
      ({ customFieldToClassTypes, deleted }) =>
        // we only want to include alwaysVisible && alwaysEditable for deleted fields
        customFieldToClassTypes.filter(({ alwaysEditable, alwaysVisible }) => {
          if (!deleted) return true
          return alwaysVisible && alwaysEditable
        })
    )
    setEditorOnSome(() =>
      cf2cts.some(({ classTypeId, users, roles }) => {
        if (classTypeId !== classType.id) return false
        if (!users.length && !roles.length) return true
        return (
          !!user?.implicitlyAuthorized({ users, roles }).authorized ||
          !!user?.admin(projectId).authorized
        )
      })
    )
  }, [classType, data?.customFields?.records, user, projectId])

  const [customFields, setCustomFields] = useState<ProjectCustomField[]>([])
  const [id2cf, setId2cf] = useState(new Map<string, ProjectCustomField>())
  const [checkboxes, setCheckboxes] = useState<ProjectCustomField[]>([])
  const [booleans, setBooleans] = useState<ProjectCustomField[]>([])
  useEffect(() => {
    if (!data?.customFields?.records) return
    const customFields = data.customFields.records.filter(
      ({ customFieldToClassTypes }) =>
        classType
          ? customFieldToClassTypes.some(
              ({ classTypeId }) => classType.id === classTypeId
            )
          : true
    )
    setCustomFields(customFields)
    setId2cf(
      customFields.reduce(
        (map, customField) => map.set(customField.id, customField),
        new Map()
      )
    )
    setCheckboxes(
      customFields.filter(
        ({ dataTypeId, metadata }) =>
          dataTypeId === CustomFieldDataType.List.id &&
          metadata?.styleId === CustomListItemStyle.Checkboxes.id
      )
    )
    setBooleans(
      customFields.filter(
        ({ dataTypeId }) => dataTypeId === CustomFieldDataType.Boolean.id
      )
    )
  }, [classType, data?.customFields?.records])

  return (
    <Provider
      value={{
        __initialized: true,
        customFields,
        customFieldIdToProjectCustomField: id2cf,
        renderedAsCheckboxes: { checkboxes, booleans },
        refetch,
        fetching,
        classType,
        toCustomFieldEditorValue,
        toMutateCustomFieldsInput,
        disabledById,
        editorOnSome,
      }}
    >
      {children}
    </Provider>
  )
}
